import type { GamePoint, NormPoint, PlacementRect, ScreenPoint } from './types';

/**
 * Coordinate transforms between the three spaces used by the prototype:
 *
 *   screen (client px)  --canvasBounds+scale-->  game (logical)  --placement-->  normalized [0,1]
 *
 * All math here is pure and resolution-independent so it can be unit-tested
 * without a browser. The renderer/input adapter supply the concrete rects.
 */

/** Describes how the game canvas maps to client pixels (from the scale manager). */
export interface CanvasMapping {
  /** Bounding rect of the <canvas> in client pixels. */
  bounds: { left: number; top: number; width: number; height: number };
  /** Logical (game) resolution the canvas represents. */
  gameWidth: number;
  gameHeight: number;
}

/** screen(client px) -> game(logical) using the canvas bounds + game resolution. */
export function screenToGame(p: ScreenPoint, m: CanvasMapping): GamePoint {
  const sx = m.bounds.width === 0 ? 1 : m.gameWidth / m.bounds.width;
  const sy = m.bounds.height === 0 ? 1 : m.gameHeight / m.bounds.height;
  return {
    x: (p.x - m.bounds.left) * sx,
    y: (p.y - m.bounds.top) * sy,
  };
}

/**
 * game(logical) -> normalized[0,1] within the artwork placement rect.
 * Values may fall outside [0,1] if the point is outside the artwork; callers
 * decide whether to clamp or reject.
 */
export function gameToNormalized(p: GamePoint, rect: PlacementRect): NormPoint {
  return {
    x: rect.width === 0 ? 0 : (p.x - rect.x) / rect.width,
    y: rect.height === 0 ? 0 : (p.y - rect.y) / rect.height,
  };
}

/** normalized[0,1] -> game(logical) within the placement rect (inverse). */
export function normalizedToGame(p: NormPoint, rect: PlacementRect): GamePoint {
  return {
    x: rect.x + p.x * rect.width,
    y: rect.y + p.y * rect.height,
  };
}

/** Convenience: screen -> normalized in one step. */
export function screenToNormalized(
  p: ScreenPoint,
  m: CanvasMapping,
  rect: PlacementRect,
): NormPoint {
  return gameToNormalized(screenToGame(p, m), rect);
}

/** normalized[0,1] -> artwork pixel coords for a given artwork canvas size. */
export function normalizedToPixels(
  p: NormPoint,
  pixelWidth: number,
  pixelHeight: number,
): { x: number; y: number } {
  return { x: p.x * pixelWidth, y: p.y * pixelHeight };
}

/** True if a normalized point lies within the unit square (the artwork box). */
export function isWithinUnitSquare(p: NormPoint): boolean {
  return p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1;
}

/** Clamp a normalized point into the unit square. */
export function clampNormalized(p: NormPoint): NormPoint {
  return {
    x: Math.min(1, Math.max(0, p.x)),
    y: Math.min(1, Math.max(0, p.y)),
  };
}
